/**
 * Configuraciones centralizadas para todos los tests
 * 
 * Este archivo contiene todas las configuraciones de tests para evitar duplicación
 * de código y mantener un sistema centralizado de feedback por rangos.
 * 
 * Sistema de Rangos Universal:
 * - Verde: Rango bajo/positivo
 * - Amarillo: Rango medio/moderado  
 * - Rojo: Rango alto/problemático
 * 
 * @author Ana Lizcano
 * @version 1.0.0
 */

// Feedback base para diferentes perfiles emocionales
const EMOTIONAL_PROFILES = {
  EMPATHETIC: "Alta sensibilidad emocional y tendencia empática.",
  BALANCED: "Perfil mixto. Procesamiento emocional equilibrado.",
  COLD: "Tendencia a un perfil emocional frío. Baja empatía o mayor control emocional."
};

// Configuraciones específicas por test
export const TEST_CONFIGURATIONS = {
  // Test de Valores (Brújula Moral)
  VALUES: {
    title: "Vector Ético-Conductual",
    categories: [
      "Principios Éticos",
      "Conformidad Normativa", 
      "Apatía Social",
      "Tendencia a la Corrupción"
    ],
    maxScore: 40,
    // Categorías donde valores altos son negativos (lógica inversa)
    inverseCategories: [
      "Apatía Social",
      "Tendencia a la Corrupción"
    ],
    feedbackConfig: {
      "Principios Éticos": {
        verde: `Requiere fortalecimiento en principios morales y éticos. ${EMOTIONAL_PROFILES.EMPATHETIC}`,
        amarillo: `Muestra valores éticos moderados con potencial de mejora. ${EMOTIONAL_PROFILES.BALANCED}`,
        rojo: `Demuestra sólidos principios éticos y morales. ${EMOTIONAL_PROFILES.COLD}`
      },
      "Conformidad Normativa": {
        verde: `Tendencia a no seguir normas establecidas. ${EMOTIONAL_PROFILES.EMPATHETIC}`,
        amarillo: `Cumplimiento moderado de normas y reglamentos. ${EMOTIONAL_PROFILES.BALANCED}`,
        rojo: `Alto cumplimiento de normas y reglamentos. ${EMOTIONAL_PROFILES.COLD}`
      },
      "Apatía Social": {
        verde: `Muestra interés y compromiso social apropiado. ${EMOTIONAL_PROFILES.EMPATHETIC}`,
        amarillo: `Nivel moderado de indiferencia hacia temas sociales. ${EMOTIONAL_PROFILES.BALANCED}`,
        rojo: `Alta indiferencia hacia responsabilidades sociales. ${EMOTIONAL_PROFILES.COLD}`
      },
      "Tendencia a la Corrupción": {
        verde: `Baja probabilidad de comportamientos corruptos. ${EMOTIONAL_PROFILES.EMPATHETIC}`,
        amarillo: `Riesgo moderado que requiere monitoreo. ${EMOTIONAL_PROFILES.BALANCED}`,
        rojo: `Alto riesgo de comportamientos no éticos. ${EMOTIONAL_PROFILES.COLD}`
      }
    }
  },

  // Test de Canciones (Resonancia Emocional)
  SONGS: {
    title: "Resonancia Emocional",
    categories: [""],
    maxScore: 6,
    // Para Resonancia Emocional, valores bajos (0-1) son positivos (empáticos)
    inverseCategories: [""],
    feedbackConfig: {
      "": {
        verde: EMOTIONAL_PROFILES.EMPATHETIC,
        amarillo: EMOTIONAL_PROFILES.BALANCED,
        rojo: EMOTIONAL_PROFILES.COLD
      }
    }
  },

  // Test de Dibujos
  DRAWING: {
    title: "Sensibilidad Relacional",
    categories: [""],
    maxScore: 10,
    feedbackConfig: {}
  }
};

// Función helper para obtener configuración por tipo de test
export const getTestConfiguration = (testType) => {
  switch (testType) {
    case "values":
      return TEST_CONFIGURATIONS.VALUES;
    case "songs":
      return TEST_CONFIGURATIONS.SONGS;
    case "drawing":
      return TEST_CONFIGURATIONS.DRAWING;
    default:
      return TEST_CONFIGURATIONS.VALUES; // Default fallback
  }
};

// Función helper para crear configuraciones personalizadas manteniendo la estructura
export const createCustomTestConfiguration = (customConfig) => {
  const baseConfig = TEST_CONFIGURATIONS.VALUES;
  
  return {
    title: customConfig.title || baseConfig.title,
    subtitle: customConfig.subtitle || baseConfig.subtitle,
    categories: customConfig.categories || baseConfig.categories,
    maxScore: customConfig.maxScore || baseConfig.maxScore,
    feedbackConfig: {
      ...baseConfig.feedbackConfig,
      ...customConfig.feedbackConfig
    }
  };
};

export default TEST_CONFIGURATIONS;